import { OFFICIAL_SOURCES } from '../officialSources';

// Deep reference content for EBC Guided Journey — researched July 2026.
// Official fees/hours from NTB park-entry-fees page & DNPWC. Verify before travel.

export const NTB_TOURIST_SERVICE_CENTER = {
	id: 'ntb-kathmandu',
	name: 'Nepal Tourism Board — Tourist Service Center',
	address: 'Pradarshani Marg (Exhibition Road), Bhrikutimandap, Kathmandu 44600',
	phone: '+977-1-4256909',
	email: 'info@ntb.org.np',
	hours: {
		summary: 'Sunday–Friday 09:00–17:00',
		closed: 'Saturdays and Nepal public holidays',
		peakSeasonTip: 'Arrive before 11:00 in October–November — counters busiest 11:00–15:00',
	},
	mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Nepal+Tourism+Board+Tourist+Service+Center+Bhrikutimandap+Kathmandu',
	whatToBring: ['Original passport', 'Passport photocopy', '2 passport-size photos', 'NPR cash (permits are NPR only)'],
	services: ['Sagarmatha National Park entry permit', 'Other national park / conservation area permits'],
	officialUrl: 'https://ntb.gov.np/en/plan-your-trip/before-you-come/park-entry-fees',
	image: {
		url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Bhrikutimandap%2C_Kathmandu.jpg/640px-Bhrikutimandap%2C_Kathmandu.jpg',
		credit: 'Wikimedia Commons — Bhrikutimandap area, Kathmandu (CC BY-SA)',
	},
	lastVerified: '2026-07-13',
};

export const MONJO_PARK_GATE = {
	id: 'monjo-gate',
	name: 'Sagarmatha National Park Entry Gate — Monjo',
	address: 'Monjo village, Khumbu Pasang Lhamu RM (on trail between Phakding and Namche)',
	hours: 'Daily during trekking season (~06:00–18:00 when checkpoint staffed)',
	mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Monjo+Sagarmatha+National+Park+checkpoint',
	note: 'Last chance to buy Sagarmatha permit if missed in Kathmandu. Do not pass without both permits.',
	lastVerified: '2026-07-13',
};

export const LUKLA_PERMIT_COUNTER = {
	id: 'lukla-permit',
	name: 'Khumbu Pasang Lhamu Rural Municipality Permit Counter',
	address: 'Near Tenzing-Hillary Airport exit, Lukla 56000',
	hours: 'Aligned with morning flight arrivals, typically 06:00–12:00',
	mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Lukla+Airport+Nepal',
	whatToBring: ['Original passport', 'NPR 2,000 cash per person'],
	image: {
		url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Lukla_Airport_2018.jpg/640px-Lukla_Airport_2018.jpg',
		credit: 'Wikimedia Commons — Tenzing-Hillary Airport, Lukla (CC BY-SA)',
	},
	lastVerified: '2026-07-13',
};

export const EBC_OFFICIAL_PDFS = [
	{
		id: 'ntb-park-fees',
		label: 'NTB — National park entry fees (official table)',
		url: 'https://ntb.gov.np/en/plan-your-trip/before-you-come/park-entry-fees',
		source: OFFICIAL_SOURCES.ntb,
		description: 'Authoritative fee table including Sagarmatha NPR 3,000 (foreigner), NPR 1,500 (SAARC), children under 10 free.',
	},
	{
		id: 'dnpwc',
		label: 'DNPWC — Department of National Parks',
		url: 'https://dnpwc.gov.np/',
		source: OFFICIAL_SOURCES.dnp,
		description: 'Issuing authority for Sagarmatha National Park regulations.',
	},
	{
		id: 'immigration-visa',
		label: 'Department of Immigration — Tourist visa',
		url: 'https://www.immigration.gov.np/en/page/tourist-visa',
		source: OFFICIAL_SOURCES.immigration,
		description: 'Visa fees, eligibility, extension rules.',
	},
	{
		id: 'immigration-online',
		label: 'Online visa application portal',
		url: 'http://nepaliport.immigration.gov.np/',
		source: OFFICIAL_SOURCES.immigrationOnline,
		description: 'Apply before flying from Germany — print confirmation for airport.',
	},
	{
		id: 'tia-arrival',
		label: 'TIA Immigration — Arrival FAQ',
		url: 'https://tia.immigration.gov.np/en/page/faq',
		source: OFFICIAL_SOURCES.immigrationTia,
		description: 'Airport arrival procedures and visa on arrival.',
	},
	{
		id: 'hra-health',
		label: 'HRA — Altitude illness prevention',
		url: 'https://himalayanrescue.org/health-problems',
		source: OFFICIAL_SOURCES.hra,
		description: 'Golden rules for acute mountain sickness from Himalayan Rescue Association.',
	},
	{
		id: 'tourist-police',
		label: 'NTB — Tourist Police information',
		url: 'https://ntb.gov.np/en/plan-your-trip/before-you-come/tourist-police',
		source: OFFICIAL_SOURCES.touristPolice,
		description: 'Tourist Police 1144 — toll-free, English, 24/7.',
	},
	{
		id: 'caan',
		label: 'CAAN — Civil Aviation (Lukla flights)',
		url: 'https://www.caan.gov.np/',
		source: OFFICIAL_SOURCES.caan,
		description: 'Domestic aviation authority — weather-related flight regulations.',
	},
];

export const EBC_WEATHER_BY_MONTH = {
	source: 'Illustrative trail-level estimates (Namche 3,440m / Gorak Shep 5,164m). Not a live forecast — check meteoblue.com or mountain-forecast.com before trek.',
	lastVerified: '2026-07-13',
	months: [
		{ month: 'Jan', namcheHigh: 5, namcheLow: -8, gorakShepHigh: -5, gorakShepLow: -20, rainMm: 10, verdict: 'Very cold, clear, few trekkers', trek: 'Possible with experience' },
		{ month: 'Feb', namcheHigh: 6, namcheLow: -6, gorakShepHigh: -3, gorakShepLow: -18, rainMm: 15, verdict: 'Cold, improving visibility', trek: 'Possible' },
		{ month: 'Mar', namcheHigh: 10, namcheLow: -2, gorakShepHigh: 2, gorakShepLow: -12, rainMm: 45, verdict: 'Warming, rhododendrons late March', trek: 'Excellent start' },
		{ month: 'Apr', namcheHigh: 14, namcheLow: 2, gorakShepHigh: 6, gorakShepLow: -8, rainMm: 55, verdict: 'Mild, some haze late April', trek: 'Excellent' },
		{ month: 'May', namcheHigh: 16, namcheLow: 5, gorakShepHigh: 8, gorakShepLow: -5, rainMm: 90, verdict: 'Warmest pre-monsoon, clouds increase', trek: 'Good early May' },
		{ month: 'Jun', namcheHigh: 17, namcheLow: 8, gorakShepHigh: 10, gorakShepLow: -2, rainMm: 200, verdict: 'Monsoon begins, wet trails', trek: 'Not recommended' },
		{ month: 'Jul', namcheHigh: 18, namcheLow: 10, gorakShepHigh: 11, gorakShepLow: 0, rainMm: 380, verdict: 'Heavy rain, flight cancellations', trek: 'Avoid' },
		{ month: 'Aug', namcheHigh: 18, namcheLow: 10, gorakShepHigh: 11, gorakShepLow: 0, rainMm: 350, verdict: 'Monsoon peak, leeches low trails', trek: 'Avoid' },
		{ month: 'Sep', namcheHigh: 15, namcheLow: 5, gorakShepHigh: 8, gorakShepLow: -5, rainMm: 120, verdict: 'Monsoon tail, clearing late Sep', trek: 'Good late September' },
		{ month: 'Oct', namcheHigh: 12, namcheLow: -2, gorakShepHigh: 4, gorakShepLow: -12, rainMm: 20, verdict: 'Clearest skies, busiest season', trek: 'Best (book early)' },
		{ month: 'Nov', namcheHigh: 8, namcheLow: -6, gorakShepHigh: 0, gorakShepLow: -15, rainMm: 15, verdict: 'Cold nights, excellent visibility', trek: 'Excellent' },
		{ month: 'Dec', namcheHigh: 6, namcheLow: -8, gorakShepHigh: -2, gorakShepLow: -18, rainMm: 10, verdict: 'Very cold, quiet trails', trek: 'Good if prepared' },
	],
	forecastLinks: [
		{ label: 'Mountain Forecast — Gorak Shep', url: 'https://www.mountain-forecast.com/peaks/Gorak-Shep/forecasts/5164' },
		{ label: 'Mountain Forecast — Namche Bazaar', url: 'https://www.mountain-forecast.com/peaks/Kongde-Ri/forecasts/6187' },
		{ label: 'Meteoblue — Lukla', url: 'https://www.meteoblue.com/en/weather/week/lukla_nepal_1283113' },
	],
};

export const EBC_CONNECTIVITY = {
	lastVerified: '2026-07-13',
	sim: [
		{
			provider: 'Ncell',
			recommendation: 'Best Khumbu trail coverage above 4,000m',
			whereToBuy: 'Ncell showroom Thamel, or airport counter on arrival',
			mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Ncell+Thamel+Kathmandu',
			packages: 'Tourist SIM NPR 100 + data pack NPR 500–1,500 (5–20 GB, 28 days)',
			trailSignal: 'Good to Namche, moderate Tengboche, intermittent above Dingboche. 4G at Namche.',
			officialUrl: OFFICIAL_SOURCES.ncell.url,
		},
		{
			provider: 'Nepal Telecom (NTC)',
			recommendation: 'Government operator, slightly weaker above 4,500m',
			whereToBuy: 'NTC office Thamel or airport',
			packages: 'Similar pricing to Ncell',
			trailSignal: 'Adequate to Namche, weak at Gorak Shep',
			officialUrl: OFFICIAL_SOURCES.ntc.url,
		},
	],
	wifi: {
		kathmandu: 'Free at most Thamel hotels',
		trail: 'Paid WiFi at teahouses NPR 200–500; slow above Tengboche. Do not rely on WiFi for emergencies.',
		tip: 'Buy Ncell SIM on arrival day. Carry power bank — charging NPR 200–500/device above Namche.',
	},
};

export const EBC_GEAR_RENTAL_SHOPS = [
	{
		name: 'Shona\'s Alpine Rental',
		area: 'Thamel, Kathmandu',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Shona%27s+Alpine+Rental+Thamel',
		items: 'Sleeping bags, down jackets, trekking poles, gaiters',
		rates: 'Sleeping bag ~$3/day, down jacket ~$2/day, poles ~$1/day',
		tip: 'Inspect zippers and cleanliness. Bargain for multi-day rental.',
	},
	{
		name: 'Rental Trekking Gear Shop (Thamel area)',
		area: 'Thamel — multiple shops on Jyatha / Paknajol',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=trekking+gear+rental+Thamel+Kathmandu',
		items: 'Full trekking kit rental',
		rates: '$8–15/day for full kit',
		tip: 'Do not rent boots unless broken in elsewhere — blisters end treks.',
	},
	{
		name: 'Purchase last-minute items',
		area: 'Thamel outdoor shops',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=outdoor+gear+shop+Thamel',
		items: 'Gloves, buff, sunscreen, batteries, adapter, snacks',
		rates: 'N/A — purchase',
		tip: 'Genuine and replica brands mixed — inspect quality.',
	},
];

export const EBC_LICENSED_AGENCIES = {
	verifyUrl: 'https://www.taan.org.np/',
	verifyLabel: 'TAAN — Trekking Agencies Association of Nepal',
	howToVerify: [
		'Ask for TAAN membership number and verify at taan.org.np',
		'Visit physical office in Thamel before paying — not airport touts',
		'Demand written itinerary, permit receipts, and Lukla ticket copies',
		'Pay deposit only (30–40%) — balance on return to Kathmandu',
	],
	redFlags: ['100% upfront cash', 'No office address', 'Price far below market', 'Airport approach', 'No TAAN registration'],
	examplesNote: 'Examples below are well-known registered operators — always verify current TAAN status yourself.',
	agencies: [
		{ name: 'Ace the Himalaya', area: 'Thamel', specialty: 'EBC group & private treks', mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Ace+the+Himalaya+Thamel' },
		{ name: 'Nepal Hiking Team', area: 'Thamel', specialty: 'Budget to mid-range EBC packages', mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Nepal+Hiking+Team+Thamel' },
		{ name: 'Himalayan Glacier', area: 'Thamel', specialty: 'Full-service EBC with gear rental', mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Himalayan+Glacier+Kathmandu' },
	],
};

export const EBC_PORTER_GUIDE = {
	lastVerified: '2026-07-13',
	hiring: 'Through the same TAAN-registered agency as your guide. Independent porter hire at Lukla is possible but agency arrangement is safer and ensures insurance.',
	costPerDay: 'USD 15–22/day (Khumbu region, 2026)',
	weightLimit: '15 kg duffel per porter (plus porter\'s own gear)',
	weightTip: 'You carry 5–8 kg daypack. Lukla flight limits checked bag to 10–15 kg.',
	tipping: '10–15% of total porter fee at end of trek, given in Kathmandu or on final day. Separate from guide tip.',
	etiquette: [
		'Porters are professionals — greet them, do not rush them on steep sections',
		'Ensure porter has adequate footwear and warm clothing — report agency if not',
		'One porter can serve two trekkers on standard EBC route',
		'Do not hand porter your daypack — duffel only',
	],
	insurance: 'Reputable agencies insure porters. Ask explicitly before booking.',
};

export const EBC_HELICOPTER_EVAC = {
	lastVerified: '2026-07-13',
	costWithoutInsurance: 'USD 5,000–15,000+ depending on altitude and weather',
	operators: ['Fishtail Air', 'Air Dynasty', 'Simrik Air', 'Manang Air'],
	procedure: [
		'1. Recognize severe AMS/HACE/HAPE — descend immediately while arranging evac',
		'2. Call insurer emergency number (authorization speeds payment)',
		'3. Guide or teahouse owner contacts helicopter operator',
		'4. Helicopter lands at nearest safe zone (Lobuche, Pheriche, Namche, Lukla)',
		'5. Fly to Kathmandu — CIWEC Clinic or Grande Hospital for insured travelers',
		'6. Keep all receipts for insurance claim',
	],
	insuranceTip: 'Policy must cover helicopter evacuation to 5,500m+. Save emergency number on paper and phone.',
	touristPolice: '1144 (toll-free, English, 24/7) — can assist coordination',
	source: OFFICIAL_SOURCES.touristPolice,
};

export const EBC_ALTITUDE_SICKNESS = {
	source: OFFICIAL_SOURCES.hra,
	lastVerified: '2026-07-13',
	goldenRules: [
		'If you feel unwell, you have altitude illness until proven otherwise',
		'Never ascend with symptoms — rest or descend',
		'Descend immediately if symptoms worsen (HACE/HAPE = life-threatening)',
		'Do not leave a sick teammate alone',
	],
	symptoms: {
		mild: 'Headache, nausea, dizziness, poor sleep, fatigue — common above 3,000m',
		severe: 'Ataxia (cannot walk straight), confusion, fluid in lungs, severe breathlessness — evacuate',
	},
	prevention: [
		'Max 500m sleeping altitude gain per day above 3,000m',
		'Acclimatization days at Namche (3,440m) and Dingboche (4,410m) — non-negotiable',
		'Climb high, sleep low',
		'Hydrate 3–4 litres/day, avoid alcohol and sedatives',
		'Diamox (acetazolamide) may help — consult doctor before travel',
	],
	aidPosts: [
		{ name: 'HRA Aid Post — Pheriche', altitude: '4,371m', services: 'AMS diagnosis, oxygen, daily lectures (seasonal)', url: 'https://himalayanrescue.org/' },
		{ name: 'Kunde Hospital (HRA-supported)', altitude: '3,840m', services: 'Emergency care, X-ray', near: 'Near Namche' },
		{ name: 'Namche medical clinic', altitude: '3,440m', services: 'Basic outpatient, altitude assessment', phone: '+977-38-540-111' },
		{ name: 'Everest ER — Lobuche/Gorak Shep area', altitude: '4,900m+', services: 'High-altitude emergency (seasonal/private)', note: 'Commercial clinic — not HRA' },
	],
};

export const EBC_EMERGENCY_CONTACTS = [
	{ label: 'Tourist Police', number: '1144', note: 'Toll-free, English, 24/7', official: true },
	{ label: 'Police', number: '100', note: 'General emergency', official: true },
	{ label: 'Ambulance', number: '102', note: 'Kathmandu valley', official: true },
	{ label: 'HRA Kathmandu office', number: '+977-1-4440292', note: 'Altitude advice, aid post coordination', official: true },
	{ label: 'CIWEC Clinic (insured travelers)', number: '+977-1-4424111', note: 'Lazimpat, Kathmandu', official: false },
	{ label: 'TIA Immigration', number: '+977-1-4113045', note: 'Airport visa issues', official: true },
	{ label: 'NTB Tourist Service Center', number: '+977-1-4256909', note: 'Permit queries', official: true },
];

export const EBC_GPX_MAP = {
	description: 'Classic EBC route: Lukla → Phakding → Namche → Tengboche → Dingboche → Lobuche → Gorak Shep → EBC → Kala Patthar → return.',
	resources: [
		{
			label: 'OpenStreetMap — Khumbu trekking area',
			url: 'https://www.openstreetmap.org/#map=12/27.850/86.750',
			type: 'interactive_map',
			note: 'Pan and zoom the full EBC corridor. Export GPX via third-party tools.',
		},
		{
			label: 'Waymarked Trails — Everest Base Camp',
			url: 'https://hiking.waymarkedtrails.org/#route?r=656484',
			type: 'gpx_route',
			note: 'Community-mapped EBC route with elevation profile. Download GPX from site.',
		},
		{
			label: 'OpenTopoMap — Khumbu terrain',
			url: 'https://opentopomap.org/#map=13/27.812/86.713',
			type: 'topo_map',
			note: 'Topographic map for offline reference — screenshot or use Organic Maps app.',
		},
	],
	offlineApps: [
		{ name: 'Organic Maps', platforms: 'iOS, Android', note: 'Download Nepal/Khumbu map region offline — free, OSM-based' },
		{ name: 'maps.me', platforms: 'iOS, Android', note: 'Offline maps — download Nepal before trek' },
		{ name: 'AllTrails / Wikiloc', platforms: 'iOS, Android', note: 'Download specific EBC GPX tracks for GPS navigation' },
	],
};

export const EBC_OFFLINE_DOWNLOADS = [
	{ id: 'packing', label: 'Packing checklist', type: 'checklist' },
	{ id: 'documents', label: 'Documents wallet checklist', type: 'checklist' },
	{ id: 'emergency', label: 'Emergency contacts card', type: 'text' },
	{ id: 'permits', label: 'Permit fee summary', type: 'text' },
	{ id: 'trek-days', label: 'Day-by-day trek summary', type: 'text' },
];

export const EBC_TRAVELER_FAQS = [
	{ q: 'Can I trek EBC solo without a guide?', a: 'Regulations have changed repeatedly. As of July 2026, verify current NTB/DoT policy before booking — guide requirements have been proposed and partially enforced on some routes. The classic EBC teahouse trail remains commonly trekked with and without guides. If policy requires a guide, hire through a TAAN-registered agency.', source: 'NTB / DoT — verify before travel', category: 'regulations' },
	{ q: 'Is TIMS card still required for Everest?', a: 'No. The Khumbu region uses the Khumbu Pasang Lhamu Rural Municipality permit (NPR 2,000) instead of TIMS. You need Sagarmatha National Park permit + Khumbu local permit only.', source: 'NTB', category: 'permits' },
	{ q: 'How much NPR cash should I carry on the trail?', a: 'Budget NPR 3,000–5,000 per day for meals and lodging. Carry NPR 50,000–80,000 total from Kathmandu for a 12-day trek. No reliable ATMs above Namche.', source: 'Practical traveler consensus', category: 'money' },
	{ q: 'Why are Lukla flights always delayed?', a: 'Tenzing-Hillary Airport has no instrument landing system. Morning fog, cloud, and wind in the Khumbu valley ground flights routinely. Delays of 1–3 days are normal — not exceptional. Build buffer days.', source: 'CAAN / practical', category: 'flights' },
	{ q: 'Can I get altitude sickness at Namche?', a: 'Yes. AMS can occur above 2,500m. Namche is 3,440m — many trekkers feel mild symptoms here. Take acclimatization day seriously.', source: 'HRA', category: 'health' },
	{ q: 'Is the water safe to drink on the trail?', a: 'No — drink bottled, boiled, or purified water only. Use purification tablets or filter. Teahouses sell boiled water (cheaper than bottles).', source: 'HRA / practical', category: 'health' },
	{ q: 'Do teahouses have showers?', a: 'Yes, paid hot showers NPR 200–500 below 4,000m. Above Lobuche, showers are rare and expensive. Wet wipes help.', source: 'Practical', category: 'trail' },
	{ q: 'Can I charge my phone on the trail?', a: 'Yes, for a fee (NPR 200–500 per device). Electricity is unreliable above Tengboche. Carry a 20,000mAh power bank.', source: 'Practical', category: 'connectivity' },
	{ q: 'What if I need a helicopter rescue?', a: 'Call insurer first. Cost without insurance: $5,000–15,000+. Tourist Police 1144 can assist. Severe AMS treatment = descend, not ascend.', source: 'HRA / Tourist Police', category: 'emergency' },
	{ q: 'Is October too crowded on EBC?', a: 'Yes — October is peak season. Book Lukla flights and first nights\' lodges early. Trails are busy but manageable. Dashain festival may affect staffing late October.', source: 'Practical', category: 'season' },
	{ q: 'Can Germans get visa on arrival?', a: 'Yes. German passport holders are eligible for tourist visa on arrival at TIA. USD 50 for 30 days. Machine-readable passport required.', source: 'Department of Immigration', category: 'visa' },
	{ q: 'Do I need crampons for EBC in November?', a: 'No. Standard trekking boots are sufficient on the main EBC trail. Microspikes optional if icy sections after snowfall.', source: 'Practical', category: 'gear' },
	{ q: 'Where is the best Everest view — EBC or Kala Patthar?', a: 'Kala Patthar (5,545m) — you cannot see Everest summit from base camp itself. Plan pre-dawn Kala Patthar ascent on clear morning.', source: 'Practical', category: 'trail' },
	{ q: 'Can I buy a SIM card at Kathmandu airport?', a: 'Yes — Ncell and NTC counters in arrivals hall. Alternatively buy in Thamel same day. Need passport copy and photo.', source: 'Practical', category: 'connectivity' },
	{ q: 'What permits do I show at Monjo?', a: 'Both Sagarmatha National Park entry permit and Khumbu Pasang Lhamu permit. Ranger checks documents before you enter the national park.', source: 'DNPWC / NTB', category: 'permits' },
];

export const EBC_TREK_DAYS_ENHANCED = [
	{ day: 1, title: 'Fly Kathmandu → Lukla, trek to Phakding', sleep: 'Phakding (2,610m)', altitude: '2,860m max', distance: '8 km', time: '3–4 hrs', food: 'Teahouse dal bhat', medical: 'Basic clinic Lukla', atm: 'Lukla only', signal: '4G Lukla', electricity: 'Yes', water: 'Bottled/purified', teahouses: 'Phakding Guest House, Snowland Lodge — basic, riverside', highlights: 'Suspension bridges, first Khumbu views', notes: 'Get Khumbu permit at Lukla airport exit before walking. Easy day — do not rush.' },
	{ day: 2, title: 'Phakding → Namche Bazaar', sleep: 'Namche (3,440m)', altitude: '3,440m', distance: '12 km', time: '5–6 hrs', food: 'Bakeries, cafés, full menus', medical: 'Namche clinic, Kunde Hospital nearby', atm: 'Namche (often empty)', signal: '4G', electricity: 'Yes (paid charge)', water: 'Purified at lodges', teahouses: 'Hotel Namche, Thamserku Lodge, Everest View Hotel (uphill)', highlights: 'Monjo permit check, Hillary Bridge, first Everest glimpse on clear days', notes: 'Sagarmatha permit verified at Monjo gate. Steep final 2 hrs to Namche.' },
	{ day: 3, title: 'Namche acclimatization day', sleep: 'Namche (3,440m)', altitude: '3,880m (Everest View Hotel)', distance: '5 km optional', time: '3–4 hrs', food: 'Full range', medical: 'Namche clinic', atm: 'Namche', signal: '4G', electricity: 'Yes', water: 'Yes', teahouses: 'Same lodge — keep your room', highlights: 'Hike to Everest View Hotel or Khumjung village + Khunde', notes: 'Sleep same altitude. HRA recommends acclimatization hike 300–500m above sleep altitude.' },
	{ day: 4, title: 'Namche → Tengboche', sleep: 'Tengboche (3,860m)', altitude: '3,860m', distance: '10 km', time: '5–6 hrs', food: 'Teahouse', medical: 'None on trail — Namche below', atm: 'No', signal: '3G moderate', electricity: 'Limited hours', water: 'Purified', teahouses: 'Tengboche Guest House, Himalaya Lodge — near monastery', highlights: 'Tengboche Monastery evening prayer (15:00–16:00)', notes: 'Trees thin out. Mani Rimdu festival here in autumn (check dates).' },
	{ day: 5, title: 'Tengboche → Dingboche', sleep: 'Dingboche (4,410m)', altitude: '4,410m', distance: '11 km', time: '5–6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: '3G weak', electricity: 'Limited', water: 'Purified', teahouses: 'Sunrise Lodge, Dingboche Resort — Imja Valley', highlights: 'Wide Imja Valley opens, Ama Dablam views', notes: 'Alpine zone begins. Watch for AMS symptoms.' },
	{ day: 6, title: 'Dingboche acclimatization', sleep: 'Dingboche (4,410m)', altitude: '4,600m (Nangkartshang peak)', distance: '6 km hike', time: '4–5 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', teahouses: 'Same lodge', highlights: 'Nangkartshang peak viewpoint (5,083m) — stunning Ama Dablam panorama', notes: 'Second critical rest day. Do not skip.' },
	{ day: 7, title: 'Dingboche → Lobuche', sleep: 'Lobuche (4,940m)', altitude: '4,940m', distance: '8 km', time: '5–6 hrs', food: 'Basic teahouse', medical: 'Everest ER nearby (seasonal)', atm: 'No', signal: '2G intermittent', electricity: 'Very limited', water: 'Purified (costly)', teahouses: 'New Paradise Lodge, Mother Earth Lodge', highlights: 'Memorial chortens for fallen climbers', notes: 'Thin air — slow pace. Last vegetation.' },
	{ day: 8, title: 'Lobuche → Gorak Shep → Everest Base Camp', sleep: 'Gorak Shep (5,164m)', altitude: '5,364m (EBC)', distance: '12 km', time: '7–8 hrs', food: 'Basic', medical: 'Everest ER', atm: 'No', signal: 'Intermittent', electricity: 'Very limited', water: 'Purified (costly)', teahouses: 'Gorak Shep lodges (basic, cold) — drop pack before EBC walk', highlights: 'Khumbu Icefall, EBC marker, glacier views', notes: 'No Everest summit view from EBC — that is Kala Patthar tomorrow.' },
	{ day: 9, title: 'Kala Patthar sunrise → descend Pheriche', sleep: 'Pheriche (4,280m)', altitude: '5,545m (Kala Patthar)', distance: '16 km', time: '8–9 hrs', food: 'Teahouse', medical: 'HRA Aid Post Pheriche', atm: 'No', signal: 'Moderate', electricity: 'Limited', water: 'Yes', teahouses: 'Pheriche lodges — attend HRA lecture if open', highlights: 'Best Everest summit view at sunrise', notes: 'Start Kala Patthar at 04:00–05:00. Long descent — knee care.' },
	{ day: 10, title: 'Pheriche → Namche', sleep: 'Namche (3,440m)', altitude: '3,440m', distance: '20 km', time: '6–7 hrs', food: 'Full range', medical: 'HRA Namche', atm: 'Namche', signal: '4G', electricity: 'Yes', water: 'Yes', teahouses: 'Any Namche lodge — hot shower!', highlights: 'Fast descent through Tengboche', notes: 'Celebrate cautiously — knees feel the descent.' },
	{ day: 11, title: 'Namche → Lukla', sleep: 'Lukla (2,860m)', altitude: '2,860m', distance: '19 km', time: '6–7 hrs', food: 'Teahouse', medical: 'Lukla clinic', atm: 'Lukla', signal: '4G', electricity: 'Yes', water: 'Yes', teahouses: 'Lukla lodges near airport', highlights: 'Final trekking day, retracing bridges', notes: 'Lukla flight likely next morning — confirm tonight.' },
	{ day: 12, title: 'Fly Lukla → Kathmandu', sleep: 'Kathmandu (1,400m)', altitude: '1,400m', distance: '—', time: '25 min flight', food: 'Everything', medical: 'Full hospitals', atm: 'Everywhere', signal: 'Full', electricity: 'Full', water: 'Yes', teahouses: 'N/A — hotel in Thamel', highlights: 'Celebration meal in Thamel', notes: 'Weather-dependent. Buffer days save missed international connections.' },
];

export const EBC_DEEP_CONTENT = {
	officeLocations: [NTB_TOURIST_SERVICE_CENTER, MONJO_PARK_GATE, LUKLA_PERMIT_COUNTER],
	officialPdfs: EBC_OFFICIAL_PDFS,
	weatherByMonth: EBC_WEATHER_BY_MONTH,
	connectivity: EBC_CONNECTIVITY,
	gearRentalShops: EBC_GEAR_RENTAL_SHOPS,
	licensedAgencies: EBC_LICENSED_AGENCIES,
	porterGuide: EBC_PORTER_GUIDE,
	helicopterEvacuation: EBC_HELICOPTER_EVAC,
	altitudeSickness: EBC_ALTITUDE_SICKNESS,
	emergencyContacts: EBC_EMERGENCY_CONTACTS,
	gpxMap: EBC_GPX_MAP,
	offlineDownloads: EBC_OFFLINE_DOWNLOADS,
	travelerFaqs: EBC_TRAVELER_FAQS,
	trekDaysEnhanced: EBC_TREK_DAYS_ENHANCED,
};
